/**
 * API Response Normalizers
 *
 * Functions that guarantee API data is in the expected format at RUNTIME.
 * TypeScript only provides compile-time type safety, NOT runtime safety.
 * When data comes from external APIs, the types are just "promises" - runtime can return anything.
 *
 * RULES:
 * 1. ALWAYS use these functions when processing data from external APIs
 * 2. Use ensureArray() for any array from API responses
 * 3. Use domain-specific normalizers for complex objects
 * 4. Defense-in-depth: Use normalizers at service, hook, AND component levels
 *
 * ARCHITECTURAL PRINCIPLE:
 * "Boundaries of the system are NOT trustworthy."
 * Data that crosses boundaries (network API, localStorage, URL params, file system) must be:
 * 1. Validated - Check if it has the expected format
 * 2. Normalized - Convert to consistent internal format
 * 3. Defaulted - Provide default values for missing fields
 */

// ============================================
// GENERIC NORMALIZERS
// ============================================

/**
 * Ensures a value is an array.
 * If undefined/null, returns empty array.
 * If not array, returns empty array and logs warning.
 *
 * @example
 * const voices = ensureArray(response.voices); // Always returns T[]
 * voices.map(...) // Safe, never crashes
 */
export function ensureArray<T>(value: T[] | undefined | null): T[] {
  if (value === undefined || value === null) {
    return [];
  }
  if (!Array.isArray(value)) {
    console.warn('[apiNormalizers] Expected array but got:', typeof value, value);
    return [];
  }
  return value;
}

/**
 * Ensures a value is a string.
 * If undefined/null, returns defaultValue.
 * If not string, converts to string and logs warning.
 */
export function ensureString(value: string | undefined | null, defaultValue = ''): string {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  if (typeof value !== 'string') {
    console.warn('[apiNormalizers] Expected string but got:', typeof value, value);
    return String(value);
  }
  return value;
}

/**
 * Ensures a value is a number.
 * If undefined/null, returns defaultValue.
 * If not number or NaN, attempts conversion and logs warning.
 */
export function ensureNumber(value: number | undefined | null, defaultValue = 0): number {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  if (typeof value !== 'number' || isNaN(value)) {
    console.warn('[apiNormalizers] Expected number but got:', typeof value, value);
    const parsed = Number(value);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return value;
}

/**
 * Ensures a value is a boolean.
 * If undefined/null, returns defaultValue.
 * If not boolean, converts to boolean and logs warning.
 */
export function ensureBoolean(value: boolean | undefined | null, defaultValue = false): boolean {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  if (typeof value !== 'boolean') {
    console.warn('[apiNormalizers] Expected boolean but got:', typeof value, value);
    return Boolean(value);
  }
  return value;
}

/**
 * Ensures a value is an object with expected properties.
 * If undefined/null, returns defaults.
 * If not object, returns defaults and logs warning.
 * Merges provided value with defaults for missing properties.
 */
export function ensureObject<T extends Record<string, unknown>>(
  value: T | undefined | null,
  defaults: T
): T {
  if (value === undefined || value === null) {
    return defaults;
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    console.warn('[apiNormalizers] Expected object but got:', typeof value, value);
    return defaults;
  }
  return { ...defaults, ...value };
}

/**
 * Ensures a value is one of the allowed values (enum-like).
 * If value is not in allowed list, returns defaultValue.
 */
export function ensureOneOf<T>(
  value: T | undefined | null,
  allowed: readonly T[],
  defaultValue: T
): T {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  if (!allowed.includes(value)) {
    console.warn('[apiNormalizers] Value not in allowed list:', value, 'allowed:', allowed);
    return defaultValue;
  }
  return value;
}

// ============================================
// DOMAIN-SPECIFIC NORMALIZERS
// ============================================

import type { IVoiceInfo, IVoiceListResponse } from '../types/voice.types';

/**
 * Normalizes a voice list response from API.
 * Ensures voices is always an array and count is a number.
 */
export function normalizeVoiceListResponse(raw: unknown): IVoiceListResponse {
  const data = raw as Partial<IVoiceListResponse> | null;
  return {
    voices: ensureArray(data?.voices),
    count: ensureNumber(data?.count, 0),
  };
}

/**
 * Normalizes a single voice info object.
 * Returns null if voice_id is missing (invalid voice).
 */
export function normalizeVoiceInfo(raw: unknown): IVoiceInfo | null {
  const data = raw as Partial<IVoiceInfo> | null;
  if (!data?.voice_id) {
    console.warn('[apiNormalizers] Voice without voice_id:', data);
    return null;
  }
  return {
    voice_id: ensureString(data.voice_id),
    name: ensureString(data.name, 'Unnamed Voice'),
    created_at: ensureString(data.created_at, new Date().toISOString()),
    duration_seconds: ensureNumber(data.duration_seconds, 0),
    file_size_bytes: ensureNumber(data.file_size_bytes, 0),
    audio_url: data.audio_url ?? null,
    language_hint: data.language_hint ?? null,
    detected_language: data.detected_language ?? null,
    detection_confidence: data.detection_confidence ?? null,
  };
}

/**
 * Normalizes an array of voices, filtering out invalid ones.
 */
export function normalizeVoices(raw: unknown): IVoiceInfo[] {
  const voices = ensureArray(raw as IVoiceInfo[] | undefined);
  return voices.map(normalizeVoiceInfo).filter((voice): voice is IVoiceInfo => voice !== null);
}

// ============================================
// IMAGE GENERATION NORMALIZERS
// ============================================

import type { ImageResult } from '../types';

/**
 * Normalizes image generation result.
 */
export function normalizeImageResult(raw: unknown): ImageResult | null {
  const data = raw as Partial<ImageResult> | null;
  if (!data?.url) {
    console.warn('[apiNormalizers] ImageResult without url:', data);
    return null;
  }
  return {
    url: ensureString(data.url),
    seed: ensureNumber(data.seed, -1),
    width: ensureNumber(data.width, 0),
    height: ensureNumber(data.height, 0),
  };
}

/**
 * Normalizes an array of image results.
 */
export function normalizeImageResults(raw: unknown): ImageResult[] {
  const results = ensureArray(raw as ImageResult[] | undefined);
  return results
    .map(normalizeImageResult)
    .filter((result): result is ImageResult => result !== null);
}

// ============================================
// UTILITY FOR WRAPPED API RESPONSES
// ============================================

/**
 * Safely extracts data from wrapped API response format.
 * Handles both { success, data } and { success, result } formats.
 */
export function extractApiData<T>(
  response: { success?: boolean; data?: T; result?: T; error?: string } | null | undefined,
  defaultValue: T
): T {
  if (!response) {
    return defaultValue;
  }
  if (response.success === false && response.error) {
    console.warn('[apiNormalizers] API returned error:', response.error);
    return defaultValue;
  }
  return (response.data ?? response.result ?? defaultValue) as T;
}
